import { MatchFlames } from '@/components/recommendations/MatchFlames';
import { matchScoreToTier } from '@/components/recommendations/match-score-tier';
import { Badge, Box, Flex, Heading, HStack, Skeleton, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

type OpportunitySignalKey = 'velocity_trend' | 'creator_match_score';

const STRONG_MATCH_SCORE = 80;
/** A trend only counts as strong once it accelerates clearly and rests on enough captures. */
const STRONG_TREND_RATIO = 1.2;
const STRONG_TREND_MIN_POINTS = 4;
/** Below this absolute variation a trend reads as flat rather than up or down. */
const STABLE_TREND_BAND = 0.05;

interface OpportunitySignal {
  key: OpportunitySignalKey;
  value: number | null;
  available: boolean;
  /** Whether the signal source is wired up at all (vs simply having no value yet). */
  activated: boolean;
}

interface OpportunityInsightsPanelProps {
  creatorMatchScore: number | null;
  velocityTrend?: number | null;
  /** Timeline point count behind the trend, used as an anti-noise gate. */
  pointsCount?: number;
  /** Hide the engagement trend row (stories have no trackable performance). */
  showTrend?: boolean;
  isLoading?: boolean;
  isIntelligenceError?: boolean;
}

export function OpportunityInsightsPanel({
  creatorMatchScore,
  velocityTrend = null,
  pointsCount = 0,
  showTrend = true,
  isLoading = false,
  isIntelligenceError = false,
}: OpportunityInsightsPanelProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return <OpportunityInsightsSkeleton title={t('mentions.intelligence.opportunityTitle')} />;
  }

  const trendSignal: OpportunitySignal = {
    key: 'velocity_trend',
    value: velocityTrend,
    available: velocityTrend !== null,
    activated: true,
  };
  const creatorSignal: OpportunitySignal = {
    key: 'creator_match_score',
    value: creatorMatchScore,
    available: creatorMatchScore !== null,
    // Brand-match being off is a distinct state from "no score yet".
    activated: creatorMatchScore !== null,
  };
  const signals: OpportunitySignal[] = showTrend ? [trendSignal, creatorSignal] : [creatorSignal];
  const recommendationLevel = getRecommendationLevel(signals, pointsCount);
  const recommendationKey = `mentions.intelligence.recommendation.${recommendationLevel}`;
  const colorPalette =
    recommendationLevel === 'high'
      ? 'green'
      : recommendationLevel === 'opportunity'
        ? 'blue'
        : 'gray';

  return (
    <Box py={5}>
      <HStack justify="space-between" mb={3} gap={3}>
        <Heading size="sm" color="gray.900">
          {t('mentions.intelligence.opportunityTitle')}
        </Heading>
        <Badge colorPalette={colorPalette} variant="subtle" textTransform="none" flexShrink={0}>
          {t(`mentions.intelligence.opportunityLevel.${recommendationLevel}`)}
        </Badge>
      </HStack>
      <Box border="1px solid" borderColor="gray.100" rounded="lg" overflow="hidden" bg="white">
        <Box
          position="sticky"
          top={0}
          zIndex={1}
          bg={`${colorPalette}.50`}
          px={3}
          py={2.5}
          data-testid="opportunity-summary-sticky"
        >
          <Text fontSize="sm" color={`${colorPalette}.800`} lineHeight="short">
            {isIntelligenceError
              ? t('mentions.intelligence.opportunityError')
              : t(recommendationKey)}
          </Text>
        </Box>
        {signals.map((signal, index) => (
          <OpportunitySignalRow
            key={signal.key}
            signal={signal}
            pointsCount={pointsCount}
            hasDivider={index > 0}
          />
        ))}
      </Box>
    </Box>
  );
}

function OpportunityInsightsSkeleton({ title }: { title: string }) {
  return (
    <Box py={5} data-testid="opportunity-loading">
      <Heading size="sm" mb={3} color="gray.900">
        {title}
      </Heading>
      <Box border="1px solid" borderColor="gray.100" rounded="lg" overflow="hidden" bg="white">
        <Skeleton h="44px" />
        <Skeleton h="56px" />
        <Skeleton h="56px" />
      </Box>
    </Box>
  );
}

function OpportunitySignalRow({
  signal,
  pointsCount,
  hasDivider,
}: {
  signal: OpportunitySignal;
  pointsCount: number;
  hasDivider: boolean;
}) {
  const { t } = useTranslation();
  const signalState = getSignalState(signal, pointsCount);

  return (
    <Flex
      align={{ base: 'flex-start', sm: 'center' }}
      justify="space-between"
      gap={3}
      borderTop={hasDivider ? '1px solid' : '0'}
      borderColor="gray.100"
      px={3}
      py={3}
      opacity={signal.available ? 1 : 0.72}
    >
      <Box minW={0}>
        <HStack gap={2} mb={0.5} flexWrap="wrap">
          <Text fontSize="sm" color="gray.800" fontWeight="medium">
            {t(`mentions.intelligence.signals.${signal.key}.label`)}
          </Text>
          <Badge
            colorPalette={signalState.colorPalette}
            variant="subtle"
            size="xs"
            textTransform="none"
          >
            {t(`mentions.intelligence.signalState.${signalState.key}`)}
          </Badge>
        </HStack>
        <Text fontSize="xs" color="gray.500">
          {t(`mentions.intelligence.signals.${signal.key}.description`)}
        </Text>
      </Box>
      <VStack align="flex-end" gap={0} flexShrink={0} minW="72px">
        <SignalValue signal={signal} />
      </VStack>
    </Flex>
  );
}

function SignalValue({ signal }: { signal: OpportunitySignal }) {
  const { t } = useTranslation();

  if (!signal.available || signal.value === null) {
    return (
      <Text fontSize="sm" fontWeight="semibold" color="gray.900">
        --
      </Text>
    );
  }

  if (signal.key === 'creator_match_score') {
    const tier = matchScoreToTier(signal.value);
    return tier ? (
      <MatchFlames tier={tier} size="sm" />
    ) : (
      <Text fontSize="sm" fontWeight="semibold" color="gray.900">
        --
      </Text>
    );
  }

  return (
    <Text fontSize="sm" fontWeight="semibold" color="gray.900">
      {formatTrend(t, signal.value)}
    </Text>
  );
}

/** Single source of truth for whether a signal clears its "strong" threshold. */
function isStrongSignal(signal: OpportunitySignal, pointsCount: number): boolean {
  if (!signal.available || signal.value === null) return false;
  switch (signal.key) {
    case 'creator_match_score':
      return signal.value >= STRONG_MATCH_SCORE;
    case 'velocity_trend':
      return signal.value >= STRONG_TREND_RATIO && pointsCount >= STRONG_TREND_MIN_POINTS;
  }
}

function getRecommendationLevel(
  signals: OpportunitySignal[],
  pointsCount: number
): 'none' | 'opportunity' | 'high' {
  const strongSignals = signals.filter((signal) => isStrongSignal(signal, pointsCount));

  if (strongSignals.length >= 2) return 'high';
  if (strongSignals.length === 1) return 'opportunity';
  return 'none';
}

/** Turns the raw trend ratio into a user-facing, benefit-led label. */
function formatTrend(
  t: (key: string, options?: Record<string, string | number>) => string,
  ratio: number
): string {
  const variation = ratio - 1;

  if (Math.abs(variation) < STABLE_TREND_BAND) {
    return t('mentions.intelligence.trend.stable');
  }

  const percent = `${variation >= 0 ? '+' : ''}${Math.round(variation * 100)}`;

  return variation > 0
    ? t('mentions.intelligence.trend.accelerating', { percent })
    : t('mentions.intelligence.trend.slowing', { percent });
}

function getSignalState(
  signal: OpportunitySignal,
  pointsCount: number
): {
  key: 'strong' | 'visible' | 'not_activated';
  colorPalette: 'green' | 'gray';
} {
  if (signal.key === 'creator_match_score' && !signal.activated) {
    return { key: 'not_activated', colorPalette: 'gray' };
  }
  if (isStrongSignal(signal, pointsCount)) {
    return { key: 'strong', colorPalette: 'green' };
  }
  return { key: 'visible', colorPalette: 'gray' };
}
